import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Layer, LayerGroup, LoginResponse, Profile, TOKEN_STORAGE } from "@app/types";

export const defaultLayer: Layer = {
  kind: 'layer',
  id: 0,
  desc: "Fondo por defecto",
  name: "Fondo predefinido",
  type: "background",
  url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  options: {},
  selected: true,
  zIndex: 0,
  listed: false,
}

const initialState = {
  layers: [] as Array<Layer | LayerGroup>,
  user: null as Profile | null,
  background: defaultLayer,
  onSidebar: false,
}

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginUser: (state, action: PayloadAction<LoginResponse>) => {
      localStorage.setItem(TOKEN_STORAGE, action.payload.token)
      let layers = action.payload.layers.map(ly => ({
        ...ly,
        selected: action.payload.active.indexOf(ly.id) !== -1,
        kind: 'layer',
        listed: action.payload.listed.indexOf(ly.id) !== -1,
        options: {
          ...ly.options,
          opacity: action.payload.opacity[`${ly.id}`] || 1
        }
      } as Layer))
      let items: Array<Layer | LayerGroup> = []
      let groups: {[s: number | string]: LayerGroup} = {}

      layers.forEach(ly => {
        let grp = action.payload.groups.find(group => group.layers.includes(ly.id))
        if (grp) {
          if (groups[grp.id]) {
            groups[grp.id].layers.push(ly)
          } else {
            groups[grp.id] = {
              ...grp,
              listed: action.payload.listed.indexOf(-grp.id) !== -1,
              kind: 'group',
              layers: [ly]
            }
          }
        } else {
          items.push(ly)
        }
      })
      Object.keys(groups)
        .map(k => groups[k])
        .filter(g => g.layers.length > 0)
        .forEach(g => items.push(g))

      const order = action.payload.order
      items.sort((a, b) => {
        let aId = -1
        let bId = -1
        if (a.kind === 'layer') {
          aId = a.id
        } else {
          aId = (a as LayerGroup).layers[0].id
        }
        if (b.kind === 'layer') {
          bId = b.id
        } else {
          bId = (b as LayerGroup).layers[0].id
        }

        return order.indexOf(aId) - order.indexOf(bId)
      })
      state.layers = items


      const bg = action.payload.layers.find(l => l.id === action.payload.background)
      if (bg !== undefined) {
        state.background = bg
      }

      let payload = {...action.payload} as any
      delete payload.layers
      delete payload.background
      state.user = {...payload}
    },
    logoutUser: (state) => {
      state.user = null
      state.layers = []
      localStorage.removeItem(TOKEN_STORAGE)
    },
    setBackground: (state, action: PayloadAction<Layer>) => {
      state.background = action.payload
    },
    setLayers: (state, action: PayloadAction<Array<Layer | LayerGroup>>) => {
      state.layers = action.payload
    },
    toggleActive: (state, action: PayloadAction<number>) => {
      state.layers.every(item => {
        if (item.kind === 'layer' && item.id === action.payload) {
          item.selected = !item.selected
          return false
        } else if (item.kind === 'group') {
          item.layers.forEach(layer => {
            if (layer.id === action.payload) {
              layer.selected = !layer.selected
              return false
            }
          })
        }
        return true
      })
    },
    toggleListed: (state, action: PayloadAction<number>) => {
      state.layers.every(item => {
        if (item.id === action.payload || item.id === -action.payload) {
          item.listed = !item.listed
          return false
        }
        return true
      })
    },
    setOpacity: (state, action: PayloadAction<[number, number]>) => {
      const [id, value] = action.payload
      state.layers.every(item => {
        if (item.kind === 'layer' && item.id === id) {
          item.options['opacity'] = value
          return false
        } else if (item.kind === 'group') {
          item.layers.forEach(layer => {
            if (layer.id === id) {
              layer.options['opacity'] = value
              return false
            }
          })
        }
        return true
      })
    },
    setListed: (state, action: PayloadAction<number[]>) => {
      state.layers.forEach(item => {
        item.listed = action.payload.indexOf(item.id) !== -1 || action.payload.indexOf(-item.id) !== -1
      })
    },

    setOnSidebar: (state, action: PayloadAction<boolean>) => {
      state.onSidebar = action.payload
    }
  }
})

export const {
  loginUser, logoutUser, setBackground, setLayers, toggleActive, toggleListed, setOpacity, setListed,
  setOnSidebar,
} = slice.actions

export default slice.reducer